import * as d3 from 'd3-selection'
import { path } from 'd3-path'

const lassoData = (dimensions, projection) => {
  let {x, y, width, height} = dimensions

  // Adjust for the 20px margins of charts
  x -= 20
  y -= 20

  let coordinates = [
    projection.invert([x, y]),
    projection.invert([x + width, y]),
    projection.invert([x + width, y + height]),
    projection.invert([x, y + height])
  ]

  // Constructor a new path that adjusts for the transform on the `g.chart`
  let p = path()
  p.rect(x, y, width, height)

  return {
    dimensions: {x, y, width, height},
    path: p.toString(),
    box: [ // x,y screen coordinates of the bounding box
      [x, y],
      [x + width, y + height]
    ],
    feature: {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [ coordinates ]
      }
    },
    coordinates
  }
}

/*
 * We can't rely on `d3.pointer` to calculate the mouse coords within this
 * svg b/c it uses `sourceEvent.clientX` which breaks in safari when the chart
 * is within a `scale(x)` parent (i.e. in a scalable canvas). Calculate the
 * coords manually respecting a parent's scale.
 */
const pointer = (e, node) => {
  let bb = node.getBoundingClientRect()
    , scale = bb.width / parseInt(d3.select(node).style('width'))
    , x = (e.sourceEvent.pageX - (document.documentElement.scrollLeft ? document.documentElement.scrollLeft : document.body.scrollLeft)) - bb.left
    , y = (e.sourceEvent.pageY - (document.documentElement.scrollTop ? document.documentElement.scrollTop : document.body.scrollTop)) - bb.top
    , coords = [x, y]

  return coords.map(coord => {
    return coord * (1 / scale) // Adjust for scale (ie. on a scalable canvas)
  })
}

class Lasso {

  constructor (ee, svg, map) {
    this.ee = ee
    this.svg = svg
    this.map = map

    // These are called by event emitters, make sure they are always
    // properly bound to `this`
    this.start = this.start.bind(this)
    this.drag = this.drag.bind(this)
    this.end = this.end.bind(this)

  }

  start (e) {
    let coords = pointer(e, this.svg.node())

    this.svg
        .selectAll('path.lasso')
        .data([{
           width: 0,
           height: 0,
           x: coords[0],
           y: coords[1]
        }])
        .enter()
        .append('path')
          .attr('class', 'lasso')

    // Update the bounding box on all features
    let {path} = this.map.datum()

    this.map
        .selectAll('path.feature')
        .each(feature => {
          feature.boundingBox = path.bounds(feature)
        })

    this.drag.apply(this, [e]) // Manually trigger a drag event so event handlers can highlight a feature if needed

    // Reset the flag so we know if they actually drag.
    this._dragged = false
  }

  drag (e) {
    this._dragged = true
    let lasso = this.svg.selectAll('path.lasso')
      , {projection} = this.map.datum()

    if (!lasso.empty()) {
      let coords = pointer(e, this.svg.node())
        , prev = lasso.datum()

      lasso.data(() => {
        let dimensions = {
            x: prev.x,
            y: prev.y,
            width: prev.width,
            height: prev.height
          }
          , diff = {
            x: coords[0] - dimensions.x,
            y: coords[1] - dimensions.y
          }

        if (diff.x < 1 || (diff.x * 2 < dimensions.width)) {
          dimensions.x = coords[0]
          dimensions.width -= diff.x
        } else {
          dimensions.width = diff.x
        }

        if (diff.y < 1 || (diff.y * 2 < dimensions.height)) {
          dimensions.y = coords[1]
          dimensions.height -= diff.y
        } else {
          dimensions.height = diff.y
        }

        let p = path()
        p.rect(dimensions.x, dimensions.y, dimensions.width, dimensions.height)
        dimensions.path = p.toString()

        return [dimensions]
      })

      lasso.attr('d', d => d.path)

      this.ee.emit('lasso:drag', lassoData(lasso.datum(), projection))
    }
  }

  end () {
    let lasso = this.svg.selectAll('path.lasso')
      , {projection} = this.map.datum()
      , data = lassoData(lasso.datum(), projection)

    lasso.remove()

    this.ee.emit('lasso:end', lassoData(lasso.datum(), projection), this._dragged)
  }
}

export default Lasso
